import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

/** A ClickHouse Cloud organization's ID: the connector's
 * `CLICKHOUSE_ORGANIZATION_ID_PATTERN` (p0-connector-clickhouse), a UUID of
 * version 1 to 8 and the standard variant, in lowercase only. So P0 refuses
 * every ID the connector would, in its own words. */
export const CLICKHOUSE_ORG_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** The connector's message for an ID it refuses, with what P0 adds:
 * lowercase, and where to find the ID. */
export const INVALID_ORGANIZATION_ID =
  "Enter the ClickHouse Cloud organization ID, a UUID such as 0b5a3c1e-6f2d-4e8a-9c7b-1d2e3f4a5b6c, in lowercase. The ClickHouse Cloud console shows it in the organization's details.";

// A connector's id names its Terraform resources, and each organization picks
// its connector by it. The pattern and its 63-character limit are a DNS
// label's (RFC 1123), as Kubernetes names an object.
const CONNECTOR_ID_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const CONNECTOR_ID_MAX_LENGTH = 63;

export const INVALID_CONNECTOR_ID = `Use lowercase letters, digits and hyphens only, starting and ending with a letter or digit, up to ${CONNECTOR_ID_MAX_LENGTH} characters.`;

/** Where an organization's API key is vaulted: the customer's own AWS Secrets
 * Manager or Google Secret Manager, in the same cloud as the organization's
 * connector. P0 stores where the key is, never the key. */
export const ClickHouseSecretVault = withoutAzureKeyVault(
  newVaultElement({ owner: "organization" })
);

/** Each artifact the install produces belongs to one of two items. The
 * deployed connector, its identity and P0's permission to invoke it belong to
 * `connector`. An organization's API key secret, and the connector's read on
 * it, belong to `organization`. */
export const ClickHouseComponents = InstallSpec({
  // One item per deployed connector, keyed by an id the customer chooses. A
  // connector serves every organization that points at it.
  //
  // The connector's identity isn't stored: its IAM role, or its service
  // account, is named after the connector (see `toConnectorIdentity`), as a
  // connector the customer deploys themselves must name it too.
  connector: ItemComponent({
    description:
      "Runs P0's ClickHouse Cloud connector in your AWS account or Google Cloud project. One connector can serve many organizations, so one is usually enough.",
    label: "Connector",
    maturity: "beta",
    schema: { hosting: ConnectorHosting },
    type: "string",
    validator: async (id) =>
      id.length <= CONNECTOR_ID_MAX_LENGTH && CONNECTOR_ID_PATTERN.test(id)
        ? undefined
        : INVALID_CONNECTOR_ID,
  }),
  // One item per ClickHouse Cloud organization, keyed by the organization's
  // ID.
  //
  // The ID has no item validator here: the install framework's error for one
  // repeats the whole request, which may hold an API key pasted in place of
  // its secret's name. The backend checks it instead
  // (`CLICKHOUSE_ORG_ID_PATTERN`).
  organization: ItemComponent({
    description:
      "A ClickHouse Cloud organization whose services P0 can grant access to, through one of your connectors",
    label: "Organization",
    maturity: "beta",
    // The connector, the vault and the secret's name can't change once the
    // item exists: the organization's Terraform grants exactly that connector
    // read on exactly that secret. To rotate the API key, the customer adds a
    // new version to the same secret. The form asks for these fields in this
    // order.
    schema: {
      connector: Element({
        description:
          "The connector that acts on this organization. P0 lists the ClickHouse Cloud connectors you've installed.",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      vault: ClickHouseSecretVault,
      apiKeySecretName: Element({
        description:
          'The name, not the ARN or the full resource name, of the secret that holds, or will hold, the API key as JSON: {"keyId": "...", "keySecret": "..."}. The organization\'s Terraform lets the connector read exactly this secret.',
        label: "API key's secret",
        step: "new",
        type: "string",
      }),
      organizationName: Element({
        description:
          "The organization's name in ClickHouse Cloud, read when the organization was last validated.",
        label: "Organization name",
        type: "generated",
      }),
      apiKeyId: Element({
        description:
          "The ID of the API key in the secret, read when the organization was last validated. P0 never stores the key's secret.",
        label: "API key ID",
        type: "generated",
      }),
      allowedDomains: Element({
        description:
          "The email domains the connector acts for, as its DOMAIN_ALLOW_PATTERN read when the organization was last validated.",
        label: "Allowed domains",
        type: "generated",
      }),
      // One `<name>: <status> (<reason>)` per service that isn't ready, joined
      // with "; ". A validation that finds at least one ready service
      // succeeds, so this is where the others are reported.
      serviceStatus: Element({
        description:
          "Each service that isn't ready for every request, with why, as of the organization's last validation. Empty when every service is ready.",
        label: "Services not ready",
        type: "generated",
      }),
    },
    type: "string",
  }),
});
